// Справочник валют: курс к базовой валюте, символ, добавление и удаление своих валют.
// Валюта для добавления выбирается дропдауном из курируемого списка
// (currencyCatalog.ts) — руками код и символ больше не вводят, это раньше
// давало опечатки вроде "GPB" и рассинхрон символа с кодом.
import { useState, type ReactElement } from 'react';
import { BASE_CURRENCY, type Currency } from '../../types';
import { useActions, useStore } from '../../store';
import { useT } from '../../i18n';
import { Modal } from './Modal';
import { CURRENCY_CATALOG, catalogSymbol } from './currencyCatalog';
import styles from './CurrenciesModal.module.css';

export interface CurrenciesModalProps {
  onClose(): void;
}

/** Валюта используется в ценах библиотеки — удалять её нельзя. */
function usageCount(library: Record<string, { kind: string }>, code: Currency): number {
  return Object.values(library).filter((seg) => {
    const money =
      seg.kind === 'lodging'
        ? (seg as unknown as { pricePerNight: { currency: string } }).pricePerNight
        : (seg as unknown as { price: { currency: string } }).price;
    return money?.currency === code;
  }).length;
}

export function CurrenciesModal({ onClose }: CurrenciesModalProps): ReactElement {
  const state = useStore();
  const actions = useActions();
  const t = useT();

  // Уже добавленные валюты из дропдобавления убираем — второй раз заводить нечего.
  const available = CURRENCY_CATALOG.filter(
    (entry) => !state.currencies.some((c) => c.code === entry.code),
  );

  const [selectedCode, setSelectedCode] = useState('');
  const [newRate, setNewRate] = useState('1');
  const [addError, setAddError] = useState<string | null>(null);

  // Если выбор ещё не сделан или выбранная валюта уже кем-то добавлена в
  // параллельной вкладке — подстраховка первым доступным кодом из списка.
  const effectiveCode = available.some((c) => c.code === selectedCode)
    ? selectedCode
    : (available[0]?.code ?? '');

  function handleAdd(): void {
    if (!effectiveCode) return;
    const rate = Number(newRate.replace(',', '.'));
    if (!Number.isFinite(rate) || !(rate > 0)) {
      setAddError(t('currencies.error.rate'));
      return;
    }
    actions.addCurrency({
      code: effectiveCode,
      symbol: catalogSymbol(effectiveCode),
      rateToBase: rate,
    });
    setSelectedCode('');
    setNewRate('1');
    setAddError(null);
  }

  return (
    <Modal
      title={t('currencies.title')}
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {t('form.close')}
        </button>
      }
    >
      <p className={styles.hint}>{t('currencies.hint', { base: BASE_CURRENCY })}</p>

      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">{t('currencies.code')}</th>
            <th scope="col">{t('currencies.symbol')}</th>
            <th scope="col">{t('currencies.rate', { base: BASE_CURRENCY })}</th>
            <th scope="col">
              <span className="visually-hidden">{t('currencies.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {state.currencies.map((c) => {
            const isBase = c.code === BASE_CURRENCY;
            const used = usageCount(state.library, c.code);
            const isDisplay = c.code === state.displayCurrency;
            const removeBlockReason = isBase
              ? t('currencies.remove.base')
              : isDisplay
                ? t('currencies.remove.display')
                : used > 0
                  ? t('currencies.remove.used', { count: used })
                  : null;

            return (
              <tr key={c.code}>
                <td className="iata">{c.code}</td>
                <td>
                  <input
                    className={`input ${styles.symbolInput}`}
                    value={c.symbol}
                    aria-label={t('currencies.symbolOf', { code: c.code })}
                    onChange={(e) => actions.updateCurrency(c.code, { symbol: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    className={`input ${styles.rateInput} mono`}
                    type="number"
                    step="0.0001"
                    min="0.0001"
                    inputMode="decimal"
                    value={isBase ? 1 : c.rateToBase}
                    disabled={isBase}
                    aria-label={t('currencies.rateOf', { code: c.code })}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      if (Number.isFinite(n) && n > 0) {
                        actions.updateCurrency(c.code, { rateToBase: n });
                      }
                    }}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    disabled={removeBlockReason !== null}
                    title={removeBlockReason ?? undefined}
                    onClick={() => actions.removeCurrency(c.code)}
                  >
                    {t('library.delete')}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {available.length > 0 ? (
        <div className={styles.addRow}>
          <label className={styles.addField}>
            <span className="field-label">{t('currencies.add.currency')}</span>
            <select
              className={`select ${styles.currencySelect}`}
              value={effectiveCode}
              onChange={(e) => setSelectedCode(e.target.value)}
            >
              {available.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {`${entry.code} — ${t(`currency.name.${entry.code}`)} (${entry.symbol})`}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.addField}>
            {/* Направление курса — самое важное поле формы: перепутанное с
                конвертацией значение молча испортит все суммы поездки. Подпись
                называет обе стороны равенства, а не абстрактное «курс». */}
            <span className="field-label">
              {t('currencies.add.rateLabel', { code: effectiveCode, base: BASE_CURRENCY })}
            </span>
            <input
              className={`input ${styles.rateInput} mono`}
              type="number"
              step="0.0001"
              min="0.0001"
              inputMode="decimal"
              value={newRate}
              placeholder="1.27"
              onChange={(e) => setNewRate(e.target.value)}
            />
          </label>
          <button type="button" className="btn" onClick={handleAdd}>
            {t('currencies.add')}
          </button>
        </div>
      ) : (
        <p className={styles.hint}>{t('currencies.add.empty')}</p>
      )}

      {addError && <p className="field-error">{addError}</p>}
    </Modal>
  );
}
